/**
 * Color palette for user-configured things (branches, stages, employees).
 * The DB stores a palette key, never raw hex; every class string is spelled
 * out here so Tailwind can see it. Do not build class names dynamically.
 */
export type PaletteEntry = {
  label: string;
  /** Tinted chip: background + border + text. */
  chip: string;
  /** Very light column/tile background. */
  soft: string;
  dot: string;
  /** Solid fill for timeline bars. */
  bar: string;
  ring: string;
  text: string;
};

export const PALETTE = {
  slate: {
    label: "Сланец",
    chip: "bg-slate-500/10 border-slate-500/30 text-slate-700 dark:text-slate-300",
    soft: "bg-slate-500/5 border-slate-500/20",
    dot: "bg-slate-500",
    bar: "bg-slate-500",
    ring: "ring-slate-500",
    text: "text-slate-700 dark:text-slate-300",
  },
  gray: {
    label: "Серый",
    chip: "bg-gray-500/10 border-gray-500/30 text-gray-700 dark:text-gray-300",
    soft: "bg-gray-500/5 border-gray-500/20",
    dot: "bg-gray-500",
    bar: "bg-gray-500",
    ring: "ring-gray-500",
    text: "text-gray-700 dark:text-gray-300",
  },
  red: {
    label: "Красный",
    chip: "bg-red-500/10 border-red-500/30 text-red-700 dark:text-red-300",
    soft: "bg-red-500/5 border-red-500/20",
    dot: "bg-red-500",
    bar: "bg-red-500",
    ring: "ring-red-500",
    text: "text-red-700 dark:text-red-300",
  },
  orange: {
    label: "Оранжевый",
    chip: "bg-orange-500/10 border-orange-500/30 text-orange-700 dark:text-orange-300",
    soft: "bg-orange-500/5 border-orange-500/20",
    dot: "bg-orange-500",
    bar: "bg-orange-500",
    ring: "ring-orange-500",
    text: "text-orange-700 dark:text-orange-300",
  },
  amber: {
    label: "Янтарный",
    chip: "bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-300",
    soft: "bg-amber-500/5 border-amber-500/20",
    dot: "bg-amber-500",
    bar: "bg-amber-500",
    ring: "ring-amber-500",
    text: "text-amber-700 dark:text-amber-300",
  },
  yellow: {
    label: "Жёлтый",
    chip: "bg-yellow-500/10 border-yellow-500/30 text-yellow-700 dark:text-yellow-300",
    soft: "bg-yellow-500/5 border-yellow-500/20",
    dot: "bg-yellow-500",
    bar: "bg-yellow-500",
    ring: "ring-yellow-500",
    text: "text-yellow-700 dark:text-yellow-300",
  },
  lime: {
    label: "Лайм",
    chip: "bg-lime-500/10 border-lime-500/30 text-lime-700 dark:text-lime-300",
    soft: "bg-lime-500/5 border-lime-500/20",
    dot: "bg-lime-500",
    bar: "bg-lime-500",
    ring: "ring-lime-500",
    text: "text-lime-700 dark:text-lime-300",
  },
  green: {
    label: "Зелёный",
    chip: "bg-green-500/10 border-green-500/30 text-green-700 dark:text-green-300",
    soft: "bg-green-500/5 border-green-500/20",
    dot: "bg-green-500",
    bar: "bg-green-500",
    ring: "ring-green-500",
    text: "text-green-700 dark:text-green-300",
  },
  emerald: {
    label: "Изумрудный",
    chip: "bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:text-emerald-300",
    soft: "bg-emerald-500/5 border-emerald-500/20",
    dot: "bg-emerald-500",
    bar: "bg-emerald-500",
    ring: "ring-emerald-500",
    text: "text-emerald-700 dark:text-emerald-300",
  },
  teal: {
    label: "Бирюзовый",
    chip: "bg-teal-500/10 border-teal-500/30 text-teal-700 dark:text-teal-300",
    soft: "bg-teal-500/5 border-teal-500/20",
    dot: "bg-teal-500",
    bar: "bg-teal-500",
    ring: "ring-teal-500",
    text: "text-teal-700 dark:text-teal-300",
  },
  cyan: {
    label: "Голубой",
    chip: "bg-cyan-500/10 border-cyan-500/30 text-cyan-700 dark:text-cyan-300",
    soft: "bg-cyan-500/5 border-cyan-500/20",
    dot: "bg-cyan-500",
    bar: "bg-cyan-500",
    ring: "ring-cyan-500",
    text: "text-cyan-700 dark:text-cyan-300",
  },
  sky: {
    label: "Небесный",
    chip: "bg-sky-500/10 border-sky-500/30 text-sky-700 dark:text-sky-300",
    soft: "bg-sky-500/5 border-sky-500/20",
    dot: "bg-sky-500",
    bar: "bg-sky-500",
    ring: "ring-sky-500",
    text: "text-sky-700 dark:text-sky-300",
  },
  blue: {
    label: "Синий",
    chip: "bg-blue-500/10 border-blue-500/30 text-blue-700 dark:text-blue-300",
    soft: "bg-blue-500/5 border-blue-500/20",
    dot: "bg-blue-500",
    bar: "bg-blue-500",
    ring: "ring-blue-500",
    text: "text-blue-700 dark:text-blue-300",
  },
  indigo: {
    label: "Индиго",
    chip: "bg-indigo-500/10 border-indigo-500/30 text-indigo-700 dark:text-indigo-300",
    soft: "bg-indigo-500/5 border-indigo-500/20",
    dot: "bg-indigo-500",
    bar: "bg-indigo-500",
    ring: "ring-indigo-500",
    text: "text-indigo-700 dark:text-indigo-300",
  },
  violet: {
    label: "Фиолетовый",
    chip: "bg-violet-500/10 border-violet-500/30 text-violet-700 dark:text-violet-300",
    soft: "bg-violet-500/5 border-violet-500/20",
    dot: "bg-violet-500",
    bar: "bg-violet-500",
    ring: "ring-violet-500",
    text: "text-violet-700 dark:text-violet-300",
  },
  purple: {
    label: "Пурпурный",
    chip: "bg-purple-500/10 border-purple-500/30 text-purple-700 dark:text-purple-300",
    soft: "bg-purple-500/5 border-purple-500/20",
    dot: "bg-purple-500",
    bar: "bg-purple-500",
    ring: "ring-purple-500",
    text: "text-purple-700 dark:text-purple-300",
  },
  fuchsia: {
    label: "Фуксия",
    chip: "bg-fuchsia-500/10 border-fuchsia-500/30 text-fuchsia-700 dark:text-fuchsia-300",
    soft: "bg-fuchsia-500/5 border-fuchsia-500/20",
    dot: "bg-fuchsia-500",
    bar: "bg-fuchsia-500",
    ring: "ring-fuchsia-500",
    text: "text-fuchsia-700 dark:text-fuchsia-300",
  },
  pink: {
    label: "Розовый",
    chip: "bg-pink-500/10 border-pink-500/30 text-pink-700 dark:text-pink-300",
    soft: "bg-pink-500/5 border-pink-500/20",
    dot: "bg-pink-500",
    bar: "bg-pink-500",
    ring: "ring-pink-500",
    text: "text-pink-700 dark:text-pink-300",
  },
  rose: {
    label: "Малиновый",
    chip: "bg-rose-500/10 border-rose-500/30 text-rose-700 dark:text-rose-300",
    soft: "bg-rose-500/5 border-rose-500/20",
    dot: "bg-rose-500",
    bar: "bg-rose-500",
    ring: "ring-rose-500",
    text: "text-rose-700 dark:text-rose-300",
  },
} satisfies Record<string, PaletteEntry>;

export type ColorKey = keyof typeof PALETTE;

export const COLOR_KEYS = Object.keys(PALETTE) as ColorKey[];

export function palette(key?: string | null): PaletteEntry {
  return (PALETTE as Record<string, PaletteEntry>)[key ?? ""] ?? PALETTE.slate;
}
